import React, { useEffect, useState } from "react";
import { BsThreeDotsVertical } from "react-icons/bs";
import { Dropdown, message } from "antd";
import {
  PlusCircleOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import GenericTable from "../../../../Components/Tables/GenericTable";
import GenericModal from "../../../../Components/Modals/GenericModal";
import {
  getAdminFuelTypes,
  deleteFuelType,
} from "../../../../API/Admin/SystemConfiguration/FuelTypes";
import usePageAccess from "../../../../Utlis/usePageAccess";

const getErrorMessage = (data, fallback) =>
  data?.message || data?.detail || fallback;

const capitalize = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : str;

const mapFuelType = (item, index) => ({
  key: String(item.id ?? index),
  name: item.name || "-",
  code: item.code || "-",
  status: capitalize(item.status) || "-",
});

const FuelTypes = ({ setBtnConfigs }) => {
  const { canEdit } = usePageAccess("system-configuration");
  const navigate = useNavigate();
  const [dataSource, setDataSource] = useState([]);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleAdd = () => {
    navigate("/add-fuel-type", { state: { from: "Fuel Types" } });
  };

  useEffect(() => {
    setBtnConfigs([
      {
        text: "Add Fuel Type",
        icon: <PlusCircleOutlined />,
        onClick: handleAdd,
      },
    ]);
  }, [setBtnConfigs]);

  useEffect(() => {
    const fetchFuelTypes = async () => {
      try {
        const res = await getAdminFuelTypes();
        const list = res?.data?.data;

        if (Math.floor(res?.status / 100) === 2 && Array.isArray(list)) {
          setDataSource(list.map(mapFuelType));
        } else {
          message.error(getErrorMessage(res?.data, "Failed to load fuel types."));
        }
      } catch (err) {
        message.error(
          getErrorMessage(err?.response?.data, "Failed to load fuel types."),
        );
      }
    };

    fetchFuelTypes();
  }, []);

  const handleEdit = (record) => {
    navigate("/edit-fuel-type", { state: { id: record.key } });
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      const res = await deleteFuelType(deleteTarget.key);

      if (Math.floor(res?.status / 100) === 2) {
        setDataSource((prev) =>
          prev.filter((item) => item.key !== deleteTarget.key),
        );
        message.success(res?.data?.message || "Fuel type deleted successfully");
      } else {
        message.error(getErrorMessage(res?.data, "Failed to delete fuel type."));
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to delete fuel type."),
      );
    } finally {
      setIsDeleteOpen(false);
      setDeleteTarget(null);
    }
  };

  const statusFilters = [...new Set(dataSource.map((item) => item.status))]
    .filter((v) => v && v !== "-")
    .map((val) => ({ text: val, value: val }));

  const columns = [
    {
      title: "Fuel Type",
      dataIndex: "name",
      key: "name",
      enableSearch: true,
    },
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      enableSearch: true,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: statusFilters,
      onFilter: (value, record) => record.status === value,
      render: (status) => (
        <span className={`status-tag ${status.toLowerCase()}`}>{status}</span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        const items = [
          {
            key: "edit",
            label: "Edit",
            style: { color: "#4F46E5", fontWeight: 500 },
            icon: <EditOutlined />,
            onClick: () => handleEdit(record),
          },
          {
            key: "delete",
            label: "Delete",
            style: { color: "#DC2626", fontWeight: 500 },
            icon: <DeleteOutlined />,
            onClick: () => {
              setDeleteTarget(record);
              setIsDeleteOpen(true);
            },
          },
        ];
        return (
          <Dropdown menu={{ items }} trigger={["click"]} placement="bottomRight">
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                width: 24,
                height: 24,
              }}
            >
              <BsThreeDotsVertical style={{ fontSize: 20, color: "#4F46E5" }} />
            </span>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className="fuel-types">
      <GenericTable
        dataSource={dataSource}
        columns={columns.filter((c) => canEdit || c.key !== "actions")}
      />
      <GenericModal
        open={isDeleteOpen}
        title="Delete Fuel Type"
        subtitle={`Are you sure you want to delete ${deleteTarget?.name || "this fuel type"}?`}
        highlightText={deleteTarget?.name || ""}
        showReason={false}
        confirmText="Delete"
        cancelText="Cancel"
        iconColor="#DC2626"
        iconBgColor="#FEE2E2"
        confirmBtnColor="#DC2626"
        onCancel={() => setIsDeleteOpen(false)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default FuelTypes;
